/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    serverTimestamp,
    setDoc,
    Timestamp,
    updateDoc,
    where,
} from "firebase/firestore";
import { auth, db } from "../../../firebase";
import { Subscription } from "./subscription.type";
import { cancelSubscription, changeSubscriptionPlan, portalSession, reactivateSubscription } from "../../../services/firebaseService";


export const SubscriptionApi = createApi({
    reducerPath: "subscriptionApi",
    tagTypes: ["subscription", "subscriptions"],
    baseQuery: fakeBaseQuery(),
    endpoints: (builder) => ({
        getSubscriptions: builder.query({
            queryFn: async ({ status }) => {
                const subscriptionsRef = collection(db, "subscriptions");
                let q = query(subscriptionsRef, orderBy("createdAt", "desc"));

                if (status) {
                    q = query(
                        subscriptionsRef,
                        where("status", "==", status),
                        orderBy("createdAt", "desc")
                    );
                }
                const snapshot = await getDocs(q);

                const subscriptions = await Promise.all(
                    snapshot.docs.map(async (docSnap) => {
                        const subscription = docSnap.data();
                        const userRef = doc(db, "users", subscription.userId);
                        const planRef = doc(db, "plans", subscription.planId);
                        const userSnap = await getDoc(userRef);
                        const planSnap = await getDoc(planRef);
                        if (userSnap.exists()) {
                            subscription.user = { uid: userSnap.id, ...userSnap.data() }
                        }
                        if (planSnap.exists()) {
                            subscription.plan = { id: planSnap.id, ...planSnap.data() }
                        }
                        return {
                            id: docSnap.id,
                            ...subscription
                        };
                    })
                );

                return { data: subscriptions as Subscription[] };
            },
            // Gestion du temps réel avec Firestore et `onSnapshot`
            async onCacheEntryAdded(
                { status },
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                let unsubscribe;

                try {
                    await cacheDataLoaded;
                    const subscriptionsRef = collection(db, "subscriptions");
                    let q = query(subscriptionsRef, orderBy("createdAt", "desc"));
                    if (status) {
                        q = query(
                            subscriptionsRef,
                            where("status", "==", status),
                            orderBy("createdAt", "desc")
                        );
                    }
                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const subscriptions = await Promise.all(

                            snapshot.docs.map(async (docSnap) => {
                                const subscription = docSnap.data();

                                const userRef = doc(db, "users", subscription.userId);
                                const planRef = doc(db, "plans", subscription.planId);
                                const userSnap = await getDoc(userRef);
                                const planSnap = await getDoc(planRef);
                                if (userSnap.exists()) {
                                    subscription.user = { uid: userSnap.id, ...userSnap.data() }
                                }
                                if (planSnap.exists()) {
                                    subscription.plan = { id: planSnap.id, ...planSnap.data() }
                                }
                                return {
                                    id: docSnap.id,
                                    ...subscription
                                };
                            })
                        );

                        // Met à jour le cache RTK Query avec les nouvelles données
                        updateCachedData(() => subscriptions as Subscription[]);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["subscriptions"],
        }),
        getSubscriptionByUserId: builder.query({
            queryFn: async (userId) => {
                if (!userId) return { data: null };
                // On effectue un premier fetch pour initialiser le cache
                try {
                    const q = query(
                        collection(db, "subscriptions"),
                        where("userId", "==", userId),
                        orderBy("endDate", "desc"),
                        limit(1)
                    );
                    const querySnapshot = await getDocs(q);

                    if (querySnapshot.empty) return { data: null };

                    let data = querySnapshot.docs[0].data();
                    const planRef = doc(db, "plans", data.planId);
                    const planSnap = await getDoc(planRef);
                    if (planSnap.exists()) {
                        data.plan = { id: planSnap.id, ...planSnap.data() }
                    }
                    return {
                        data: {
                            ...data,
                            id: querySnapshot.docs[0].id,
                            endDate: data.endDate?.toMillis(),
                            startDate: data.startDate?.toMillis(),
                        } as Subscription
                    };
                } catch (error: any) {
                    return { error: { status: 500, message: error.message } };
                }
            },
            async onCacheEntryAdded(
                userId,
                { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
            ) {
                if (!userId) return;
                let unsubscribe;

                try {
                    await cacheDataLoaded;

                    const q = query(
                        collection(db, "subscriptions"),
                        where("userId", "==", userId),
                        orderBy("endDate", "desc"),
                        limit(1)
                    );
                    unsubscribe = onSnapshot(q, async (snapshot) => {
                        const subscriptions = await Promise.all(
                            snapshot.docs.map(async (docSnap) => {
                                const subscription = docSnap.data();
                                const userRef = doc(db, "users", subscription.userId);
                                const planRef = doc(db, "plans", subscription.planId);
                                const userSnap = await getDoc(userRef);
                                const planSnap = await getDoc(planRef);
                                if (userSnap.exists()) {
                                    subscription.user = { uid: userSnap.id, ...userSnap.data() }
                                }
                                if (planSnap.exists()) {
                                    subscription.plan = { id: planSnap.id, ...planSnap.data() }
                                }
                                return {
                                    id: docSnap.id,
                                    ...subscription
                                };
                            })
                        );

                        // Met à jour le cache RTK Query avec les nouvelles données
                        updateCachedData(() => subscriptions[0] as Subscription);
                    });
                } catch (error) {
                    console.error("Erreur de souscription:", error);
                }

                // Nettoyer l'écouteur Firestore quand le cache est supprimé
                await cacheEntryRemoved;
                if (unsubscribe) unsubscribe();
            },
            providesTags: ["subscription"],
        }),
        addSubscription: builder.mutation({
            queryFn: async (selectedPlan) => {
                try {
                    const user = auth.currentUser;
                    if (!user) {
                        return { error: { message: "Aucun utilisateur connecté." } };
                    }
                    const now = new Date();
                    const trialEndDate = new Date();
                    trialEndDate.setDate(now.getDate() + 14);
                    const docRef = doc(db, "subscriptions", user.uid);
                    await setDoc(docRef, {
                        userId: user.uid,
                        planId: selectedPlan?.id,
                        planDuration: selectedPlan?.durationMonths || 1,
                        status: "trialing",
                        createdAt: Timestamp.fromDate(now),
                        startDate: Timestamp.fromDate(now),
                        trialStartDate: Timestamp.fromDate(now),
                        trialEndDate: Timestamp.fromDate(trialEndDate),
                        endDate: Timestamp.fromDate(trialEndDate),
                        hasPack: false,
                        selectedPacks: [],
                        autoRenew: true,
                        stripeCustomerId: null,
                        stripePaymentMethodId: null,
                        ...(selectedPlan?.target === "INSTITUTE" && {
                            schoolTrialOnboardingPromptHandled: false,
                        }),
                        updatedAt: Timestamp.fromDate(now)
                    }, { merge: true });
                    const subSnap = await getDoc(docRef);
                    return {
                        data: {
                            subscription: {
                                id: subSnap.id,
                                plan: selectedPlan,
                                ...subSnap.data()
                            },
                            message: "Période d'essai activée.",
                        },
                    };

                } catch (error: any) {
                    return { error: { status: "erreur", message: error.message } };
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        editSubscription: builder.mutation({
            queryFn: async ({ id, ...data }) => {
                try {
                    const docRef = doc(db, "subscriptions", id);
                    await updateDoc(docRef, { ...data, updatedAt: serverTimestamp() });
                    return {
                        data: { message: `L'abonnement a été modifié.`, success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "TOGGLE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        toggleSubscription: builder.mutation({
            queryFn: async ({ id, autoRenew }) => {
                try {
                    const docRef = doc(db, "subscriptions", id);
                    await updateDoc(docRef, { autoRenew, updatedAt: serverTimestamp() });
                    return {
                        data: { message: `Abonnement résilié.`, success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "TOGGLE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        // Supprimer
        deleteSubscription: builder.mutation({
            queryFn: async (uid) => {
                try {
                    const docRef = doc(db, "subscriptions", uid);
                    await deleteDoc(docRef);
                    return {
                        data: { uid, message: "Abonnement supprimé.", success: true },
                    };
                } catch (error: any) {
                    return { error: { status: "DELETE_ERROR", message: error.message } };
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        changeSubscriptionPlan: builder.mutation({
            queryFn: async (data: { newPlanId: string }) => {
                try {
                    const response: any = await changeSubscriptionPlan(data);
                    return {
                        data: {
                            message: response?.message,
                        },
                    };
                } catch (error: any) {
                    return error;
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        cancelSubscription: builder.mutation({
            queryFn: async () => {
                try {
                    const response: any = await cancelSubscription();
                    return {
                        data: {
                            message: response?.message,
                        },
                    };
                } catch (error: any) {
                    return error;
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        reactivateSubscription: builder.mutation({
            queryFn: async () => {
                try {
                    const response: any = await reactivateSubscription();
                    return {
                        data: {
                            message: response?.message,
                        },
                    };
                } catch (error: any) {
                    return error;
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
        createPortalSession: builder.mutation({
            queryFn: async (data: { returnUrl: string }) => {
                try {
                    const response: any = await portalSession(data.returnUrl);
                    return {
                        data: {
                            url: response?.url,
                        },
                    };
                } catch (error: any) {
                    return error;
                }
            },
            invalidatesTags: ["subscription", "subscriptions"],
        }),
    }),
});

export const {
    useGetSubscriptionsQuery,
    useDeleteSubscriptionMutation,
    useLazyGetSubscriptionByUserIdQuery,
    useToggleSubscriptionMutation,
    useEditSubscriptionMutation,
    useAddSubscriptionMutation,
    useChangeSubscriptionPlanMutation,
    useCancelSubscriptionMutation,
    useCreatePortalSessionMutation,
    useReactivateSubscriptionMutation
} = SubscriptionApi;
